import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AnalyticsAssistant from "./components/assistant/AnalyticsAssistant";
import { PeriodFilter, TransportModeFilter } from "./components/filters/AnalyticsFilters";
import { OverviewPanel, TendersPanel, CarriersPanel, DirectionsPanel } from "./panels/AnalyticsPanels";
import { getInitialPeriod } from "./utils/analyticsFormat";
import { useAssistantChat } from "./hooks/useAssistantChat";
import styles from "./OrderAnalyticsPage.module.scss";

const panels = {
  overview: OverviewPanel,
  tenders: TendersPanel,
  carriers: CarriersPanel,
  directions: DirectionsPanel,
};

export default function OrderAnalyticsPage() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [period, setPeriod] = useState(getInitialPeriod);
  const [mode, setMode] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  const assistant = useAssistantChat(period);
  const loaded = user?.permissions !== undefined;
  const allowed = Array.isArray(user?.permissions) && user.permissions.includes("analityca_view");
  const validPeriod = Boolean(period.startDate && period.endDate && period.startDate <= period.endDate);
  const tabs = [
    ["overview", t("analyticsNew.tabs.overview", "Обзор")],
    // ["tenders", t("analyticsNew.tabs.tenders", "Тендеры")],
    ["carriers", t("analyticsNew.tabs.carriers", "Перевозчики")],
    ["directions", t("analyticsNew.tabs.directions", "Направления")],
  ];
  const Panel = panels[activeTab];
  function navigateTabs(event, index) {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = tabs.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setActiveTab(tabs[next][0]);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
  }
  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
        <header className={styles.topbar}>
          <div className={styles.brand}>
            <h1>{t("analyticsNew.title", "Аналитика")}</h1>
          </div>
        </header>
        {!loaded ? (
          <div className={styles.state} role="status">
            {t("analyticsNew.loading", "Загрузка аналитики…")}
          </div>
        ) : !allowed ? (
          <div className={styles.state} role="alert">
            {t("analyticsNew.accessDenied", "У вас нет доступа к аналитике")}
          </div>
        ) : (
          <div className={styles.layout}>
            <div className={styles.dashboard}>
              <div className={styles.toolbar}>
                <div
                  className={styles.tabs}
                  role="tablist"
                  aria-label={t("analyticsNew.sections", "Разделы аналитики")}
                >
                  {tabs.map(([key, title], index) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      id={`analytics-tab-${key}`}
                      aria-controls={`analytics-panel-${key}`}
                      aria-selected={key === activeTab}
                      tabIndex={key === activeTab ? 0 : -1}
                      className={`${styles.tab} ${key === activeTab ? styles.active : ""}`}
                      onClick={() => setActiveTab(key)}
                      onKeyDown={(event) => navigateTabs(event, index)}
                    >
                      {title}
                    </button>
                  ))}
                </div>
                <div className={styles.tools}>
                  <TransportModeFilter value={mode} onChange={setMode} />
                  <PeriodFilter period={period} setPeriod={setPeriod} validPeriod={validPeriod} />
                </div>
              </div>
              <div
                role="tabpanel"
                id={`analytics-panel-${activeTab}`}
                aria-labelledby={`analytics-tab-${activeTab}`}
                tabIndex={0}
              >
                {validPeriod ? (
                  <Panel
                    period={period}
                    mode={mode}
                    onAskAssistant={assistant.ask}
                    assistantPending={assistant.pending}
                  />
                ) : (
                  <div className={styles.state} role="alert" id="analytics-date-error">
                    {!period.startDate || !period.endDate
                      ? t("analyticsNew.requiredDates", "Укажите начало и конец периода")
                      : t("analyticsNew.invalidDates", "Начало периода не может быть позже конца")}
                  </div>
                )}
              </div>
            </div>
            <AnalyticsAssistant chat={assistant} period={period} />
          </div>
        )}
      </div>
    </div>
  );
}
